import { Button, ButtonVariant } from "components/Form/Button";
import { Input } from "components/Form/Input";
import { InputPassword } from "components/Form/InputPassword";
import { Field } from "components/Form/Field";
import { Select } from "components/Form/Select";
import { PSC, pcsGuardianGroupKey } from "components/PCS";
import { useCallback, useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  apiGetGuardianWards,
  apiRegisterWard,
  apiSendVerificationCode,
  apiSwitchUser,
  apiVerifyCode,
} from "lib/api";
import { toast } from "react-toastify";
import { Storage, StorageKey } from "lib/storage";
import { AuthContext } from "contexts/AuthContext";

enum Gender {
  Male = "male",
  Female = "female",
  Other = "other",
  "Prefer not to say" = "unknown",
}

enum Ethnicity {
  Black = "black",
  White = "white",
  "Coloured (South Africa)" = "coloured",
  "Middle Eastern" = "middle_eastern",
  "North African" = "north_african",
  Hispanic = "hispanic",
  "South Asian" = "south_asian",
  "Central Asian" = "central_asian",
  "East Asian" = "east_asian",
  "Southeast Asian" = "southeast_asian",
  "Pacific Islander" = "pacific_islander",
  "Māori" = "maori",
  "Aboriginal Australian" = "aboriginal_australian",
  "Native American" = "native_american",
  "Indigenous South American" = "indigenous_south_american",
  Other = "other",
  "Prefer not to say" = "unknown",
}

type WardFormValue = {
  firstname: string;
  lastname: string;
  mothers_name: string;
  fathers_name: string;
  date_of_birth: string;
  gender: Gender;
  ethnicity: Array<Ethnicity>;
  email: string;
  password: string;
  password_confirm: string;
  guardian_consent: boolean;
};

type Ward = {
  user_id: string;
  firstname: string;
  lastname: string;
  date_of_birth: string;
  email: string;
};

const nameValidation = {
  required: true,
  minLength: 2,
  pattern: {
    value: /^[A-Za-z\s'-]+$/,
    message: "Only letters, spaces, hyphens and apostrophes allowed",
  },
};

const initialValidation = {
  required: true,
  pattern: {
    value: /^[A-Za-z]$/,
    message: "Only letters allowed",
  },
};

const handleNameKeyDown = (e: React.KeyboardEvent) => {
  if (
    !/[A-Za-z\s'-]/.test(e.key) &&
    !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
  ) {
    e.preventDefault();
  }
};

const handleInitialKeyDown = (e: React.KeyboardEvent) => {
  if (
    !/[A-Za-z]/.test(e.key) &&
    !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
  ) {
    e.preventDefault();
  }
};

enum Screen {
  Main,
  RegisterForm,
}

export function LinkedAccounts(props: { onClose: () => void }) {
  const [screen, setScreen] = useState(Screen.Main);
  const [wards, setWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { token, setToken } = useContext(AuthContext);
  const isWard = !!token?.guardian_user_id;

  const handleSwitchToWard = useCallback(
    async (wardUserId: string) => {
      setLoading(true);
      try {
        const newToken = await apiSwitchUser(wardUserId);
        Storage.set(StorageKey.TOKEN, newToken);
        setToken(newToken);
        window.location.reload();
      } catch (err: any) {
        toast.error(err?.detail || "Failed to switch user");
        setLoading(false);
      }
    },
    [setToken]
  );

  const fetchWards = useCallback(async () => {
    try {
      const result = await apiGetGuardianWards();
      setWards(result);
    } catch {
      // Ignore errors on fetch
    }
  }, []);

  useEffect(() => {
    fetchWards();
  }, [fetchWards]);

  if (screen === Screen.RegisterForm) {
    return (
      <RegisterWardForm
        onClose={() => setScreen(Screen.Main)}
        onSuccess={() => {
          fetchWards();
          setScreen(Screen.Main);
        }}
      />
    );
  }

  if (showConfirm) {
    return (
      <div className="grid gap-6 text-center">
        <div className="grid gap-2">
          <h3 className="font-medium">Add Managed Profile</h3>
          <p className="text-14 text-454545">
            You&apos;re about to create a managed profile for your dependent.
            Continue?
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant={ButtonVariant.FLAT}
            onClick={() => setShowConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowConfirm(false);
              setScreen(Screen.RegisterForm);
            }}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <div className="grid gap-2 text-center">
        <h3 className="font-medium">Handle linked accounts</h3>
      </div>

      {wards.length > 0 && (
        <div className="flex flex-col gap-2">
          {wards.map((ward) => (
            <button
              key={ward.user_id}
              type="button"
              disabled={loading}
              onClick={() => handleSwitchToWard(ward.user_id)}
              className="flex items-center justify-between border border-f4f7ff rounded-lg p-3 w-full text-left hover:bg-f4f7ff/50 transition-colors cursor-pointer"
            >
              <div>
                <div className="font-medium text-14">
                  {ward.firstname} {ward.lastname}
                </div>
                <div className="text-12 text-454545">{ward.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {isWard && (
        <p className="text-14 text-858494 text-center">
          You are registered as a minor account and cannot manage linked
          profiles.
        </p>
      )}

      {!isWard && (
        <div className="flex flex-col gap-2">
          <Button
            variant={ButtonVariant.FLAT}
            className="border border-f4f7ff"
            onClick={() => setShowConfirm(true)}
          >
            Add Managed Profile
          </Button>
        </div>
      )}
      <Button onClick={props.onClose}>Done</Button>
    </div>
  );
}

function RegisterWardForm(props: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [pendingData, setPendingData] = useState<WardFormValue | null>(null);
  const [code, setCode] = useState(["", "", "", "", ""]);
  const [processing, setProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    setError,
    formState: { isValid, errors },
  } = useForm<WardFormValue>({
    mode: "onChange",
  });

  const watchPassword = watch("password");
  const watchGender = watch("gender");
  const watchEthnicity = watch("ethnicity");

  useEffect(() => {
    register("gender", { required: true });
    register("ethnicity", { required: true });
  }, [register]);

  const onSubmit = useCallback<SubmitHandler<WardFormValue>>(
    async (data) => {
      const [year, month] = data.date_of_birth.split("-").map(Number);
      const today = new Date();
      const age =
        today.getFullYear() - year - (today.getMonth() + 1 < month ? 1 : 0);

      if (age >= 18) {
        toast.error("The ward must be under 18 years old");
        return;
      }

      setLoading(true);
      try {
        await apiSendVerificationCode(data.email, false);
        toast.success("Verification code sent to " + data.email);
        setPendingData(data);
      } catch (err: any) {
        if (err?.code === "email_exists") {
          setError("email", { message: "This email is already registered." });
        } else {
          toast.error(err?.detail || "Failed to send verification code");
        }
      } finally {
        setLoading(false);
      }
    },
    [setError]
  );

  const handleCodeChange = useCallback(
    (index: number, value: string) => {
      if (value.length <= 1 && /^[0-9]*$/.test(value)) {
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Auto-focus next input
        if (value && index < 4) {
          const nextInput = document.getElementById(`ward-code-${index + 1}`);
          nextInput?.focus();
        }
      }
    },
    [code]
  );

  const handleCodeKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !code[index] && index > 0) {
        const prevInput = document.getElementById(`ward-code-${index - 1}`);
        prevInput?.focus();
      }
    },
    [code]
  );

  const handleVerifyCode = useCallback(async () => {
    if (!pendingData) return;

    const codeString = code.join("");
    if (codeString.length !== 5) {
      toast.error("Please enter the complete 5-digit code");
      return;
    }

    setProcessing(true);
    try {
      await apiVerifyCode(pendingData.email, codeString);

      // Email verified — now register the ward
      await apiRegisterWard(
        {
          firstname: pendingData.firstname,
          lastname: pendingData.lastname,
          mothers_name: pendingData.mothers_name,
          fathers_name: pendingData.fathers_name,
          date_of_birth: pendingData.date_of_birth,
          gender: pendingData.gender,
          ethnicity: JSON.stringify(pendingData.ethnicity),
          legal_status: "minor",
          agreement: true,
        },
        pendingData.email,
        pendingData.password
      );
      toast.success("Ward registered successfully");
      props.onSuccess();
    } catch (err: any) {
      if (err?.code === "email_exists") {
        toast.error("This email is already registered.");
        setPendingData(null);
      } else {
        toast.error(
          err?.message ||
            err?.detail ||
            "Verification failed. Please try again."
        );
      }
      setCode(["", "", "", "", ""]);
      document.getElementById("ward-code-0")?.focus();
    } finally {
      setProcessing(false);
    }
  }, [code, pendingData, props]);

  const handleResendCode = useCallback(async () => {
    if (!pendingData) return;
    setProcessing(true);
    try {
      await apiSendVerificationCode(pendingData.email, false);
      toast.success("New verification code sent to " + pendingData.email);
      setCode(["", "", "", "", ""]);
      document.getElementById("ward-code-0")?.focus();
    } catch {
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setProcessing(false);
    }
  }, [pendingData]);

  // OTP verification screen
  if (pendingData) {
    return (
      <div className="grid gap-6 text-center">
        <div className="grid gap-2">
          <h3 className="font-medium">Verify email</h3>
          <p className="text-12 text-454545">
            We&apos;ve sent a 5-digit code to
            <br />
            <strong>{pendingData.email}</strong>
          </p>
        </div>

        <div className="grid gap-4">
          <p className="text-12 text-454545">Enter verification code</p>

          <div className="flex justify-center gap-3">
            {code.map((digit, index) => (
              <input
                key={index}
                id={`ward-code-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                disabled={processing}
                className="w-12 h-12 text-center text-20 font-semibold border border-d1d5db rounded-lg focus:outline-none focus:ring-2 focus:ring-3461fd focus:border-transparent disabled:bg-f4f7ff disabled:cursor-not-allowed"
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>

        <div className="grid gap-2">
          <p className="text-12 text-454545">Didn&apos;t receive the code?</p>
          <Button
            variant={ButtonVariant.FLAT_BORDER}
            onClick={handleResendCode}
            disabled={processing}
            type="button"
          >
            Resend Code
          </Button>
        </div>

        <p className="text-12 text-858494">
          Check your spam folder if you don&apos;t see the email. The
          verification code is valid for 10 minutes.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant={ButtonVariant.FLAT}
            onClick={() => {
              setPendingData(null);
              setCode(["", "", "", "", ""]);
            }}
            disabled={processing}
          >
            Back
          </Button>
          <Button
            onClick={handleVerifyCode}
            disabled={code.join("").length !== 5 || processing}
          >
            {processing ? "Verifying..." : "Verify"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
      <div className="text-center">
        <h3 className="font-medium">Register a ward</h3>
        <p className="text-12 text-454545 mt-1">
          Enter the ward's information to link them to your account
        </p>
      </div>

      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" className="compact-field">
            <Input
              disabled={loading}
              {...register("firstname", nameValidation)}
              className="h-10"
              onKeyDown={handleNameKeyDown}
              placeholder="First name"
            />
          </Field>
          <Field label="Last name" className="compact-field">
            <Input
              disabled={loading}
              {...register("lastname", nameValidation)}
              className="h-10"
              onKeyDown={handleNameKeyDown}
              placeholder="Last name"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Mother's initial" className="compact-field">
            <Input
              disabled={loading}
              {...register("mothers_name", initialValidation)}
              className="h-10"
              maxLength={1}
              onKeyDown={handleInitialKeyDown}
              placeholder="M"
            />
          </Field>
          <Field label="Father's initial" className="compact-field">
            <Input
              disabled={loading}
              {...register("fathers_name", initialValidation)}
              className="h-10"
              maxLength={1}
              onKeyDown={handleInitialKeyDown}
              placeholder="F"
            />
          </Field>
        </div>

        <Field
          label="Date of birth"
          className="compact-field"
          description={errors.date_of_birth?.message}
          descriptionClassName="text-fd380f text-12"
        >
          <Input
            disabled={loading}
            type="month"
            {...register("date_of_birth", {
              required: true,
              validate: (value) => {
                const [year, month] = value.split("-").map(Number);
                const today = new Date();
                const age =
                  today.getFullYear() -
                  year -
                  (today.getMonth() + 1 < month ? 1 : 0);
                if (age < 0) return "Invalid date";
                if (age >= 18) return "Ward must be under 18 years old";
                return true;
              },
            })}
            className="h-10"
            onChange={async (e) => {
              setValue("date_of_birth", e.target.value);
              await trigger("date_of_birth");
            }}
          />
        </Field>

        <Field label="Gender" className="compact-field">
          <Select
            options={Gender}
            value={watchGender}
            renderValue={(v) =>
              Object.entries(Gender).find((i) => i[1] === v)?.[0]
            }
            onChange={(v) => {
              setValue("gender", v as Gender, { shouldValidate: true });
            }}
            className="h-10"
          />
        </Field>

        <Field
          label="Ethnicity"
          className="compact-field"
          description="Select up to 3"
        >
          <Select
            multiple
            options={Ethnicity}
            value={watchEthnicity}
            renderValue={(v) => {
              if (!v || v.length === 0) return "Select ethnicity";
              return v
                .map(
                  (i) => Object.entries(Ethnicity).find((j) => j[1] === i)?.[0]
                )
                .join(", ");
            }}
            onChange={(v) => {
              if (v.length <= 3) {
                setValue("ethnicity", v as Ethnicity[], {
                  shouldValidate: true,
                });
              }
            }}
            className="h-10"
          />
        </Field>

        <div className="border-t border-f4f7ff pt-3">
          <p className="text-12 text-454545 mb-3">
            The ward's login credentials
          </p>

          <div className="grid gap-3">
            <Field
              label="Email"
              className="compact-field"
              description={errors.email?.message}
              descriptionClassName="text-fd380f text-12"
            >
              <Input
                disabled={loading}
                type="email"
                {...register("email", {
                  required: true,
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please provide a valid email",
                  },
                })}
                className="h-10"
                placeholder="minor@example.com"
              />
            </Field>

            <Field label="Password" className="compact-field">
              <InputPassword
                disabled={loading}
                autoCapitalize="off"
                autoComplete="new-password"
                {...register("password", {
                  required: true,
                  minLength: 8,
                  validate: {
                    hasUpperCase: (v) =>
                      /[A-Z]/.test(v) || "Must contain uppercase letter",
                    hasLowerCase: (v) =>
                      /[a-z]/.test(v) || "Must contain lowercase letter",
                    hasNumber: (v) => /[0-9]/.test(v) || "Must contain number",
                  },
                })}
              />
            </Field>

            <div className="space-y-2 p-3 bg-f4f7ff rounded-lg">
              <p className="text-12 font-semibold text-454545">
                Password requirements:
              </p>
              <ul className="space-y-1.5">
                {[
                  {
                    label: "At least 8 characters",
                    test: (p: string) => p.length >= 8,
                  },
                  {
                    label: "One uppercase letter",
                    test: (p: string) => /[A-Z]/.test(p),
                  },
                  {
                    label: "One lowercase letter",
                    test: (p: string) => /[a-z]/.test(p),
                  },
                  { label: "One number", test: (p: string) => /[0-9]/.test(p) },
                ].map((req, i) => {
                  const met = watchPassword ? req.test(watchPassword) : false;
                  return (
                    <li key={i} className="flex items-center gap-2 text-12">
                      <span
                        className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          met
                            ? "bg-green-500 text-white"
                            : "bg-d1d5db text-858494"
                        }`}
                      >
                        {met ? "✓" : "○"}
                      </span>
                      <span className={met ? "text-green-700" : "text-858494"}>
                        {req.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <Field
              label="Confirm password"
              className="compact-field"
              description={errors.password_confirm?.message}
              descriptionClassName="text-fd380f text-12"
            >
              <InputPassword
                disabled={loading}
                autoCapitalize="off"
                autoComplete="new-password"
                {...register("password_confirm", {
                  required: true,
                  validate: (value) =>
                    value === watchPassword || "Passwords do not match",
                })}
              />
            </Field>
          </div>
        </div>
      </div>

      <PSC
        groupKey={pcsGuardianGroupKey}
        onDisplayed={() => register("guardian_consent", { required: true })}
        onValid={() =>
          setValue("guardian_consent", true, { shouldValidate: true })
        }
        onInvalid={() =>
          setValue("guardian_consent", false, { shouldValidate: true })
        }
        onError={() =>
          setValue("guardian_consent", true, { shouldValidate: true })
        }
      />

      <div className="grid gap-2 grid-cols-2">
        <Button
          type="button"
          variant={ButtonVariant.FLAT}
          onClick={props.onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!isValid || loading}>
          Register
        </Button>
      </div>
    </form>
  );
}
